const TAU = Math.PI * 2;
const MUTE_STORAGE_KEY = 'cup-cascade-muted';

type AudioContextConstructor = new (contextOptions?: AudioContextOptions) => AudioContext;

export class AudioSystem {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private uiGain: GainNode | null = null;
  private ambienceGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private readonly activeSources = new Set<AudioScheduledSourceNode>();
  private readonly ambienceSources: AudioBufferSourceNode[] = [];

  private muted = this.readStoredMute();
  private unlocked = false;
  private unlockListenersInstalled = false;
  private disposed = false;
  private lastRimHitAt = -Infinity;
  private lastBoardHitAt = -Infinity;
  private rimVariant = 0;

  constructor() {
    this.installUnlockListeners();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  async unlock(): Promise<void> {
    if (this.disposed) return;

    try {
      if (!this.context) {
        const ContextClass = this.getAudioContextClass();
        if (!ContextClass) return;
        this.context = new ContextClass({ latencyHint: 'interactive' });
        this.createAudioGraph(this.context);
      }

      if (this.context.state === 'suspended') await this.context.resume();
      if (this.context.state !== 'running') {
        this.installUnlockListeners();
        return;
      }

      this.unlocked = true;
      this.removeUnlockListeners();
      this.startAmbience();
      this.applyMuteLevel(true);
    } catch {
      this.unlocked = false;
      this.installUnlockListeners();
    }
  }

  setMuted(muted: boolean): void {
    if (this.disposed) return;
    this.muted = muted;
    this.storeMute(muted);
    this.applyMuteLevel(false);
    if (!muted) void this.unlock();
  }

  toggleMuted(): boolean {
    const nextMuted = !this.muted;

    if (nextMuted) {
      this.uiClick();
      this.setMuted(true);
    } else {
      this.setMuted(false);
      void this.unlock().then(() => this.uiClick());
    }

    return nextMuted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  uiClick(): void {
    const context = this.getPlayableContext();
    if (!context || !this.uiGain) return;

    this.playTone(context, this.uiGain, {
      startFrequency: 760,
      endFrequency: 470,
      duration: 0.075,
      volume: 0.034,
      type: 'triangle',
    });
    this.playTone(context, this.uiGain, {
      startFrequency: 1240,
      endFrequency: 960,
      duration: 0.045,
      volume: 0.013,
      type: 'sine',
      delay: 0.012,
    });
  }

  throw(power: number): void {
    const context = this.getPlayableContext();
    if (!context || !this.sfxGain) return;
    const strength = this.normalizePower(power);

    this.playNoise(context, this.sfxGain, {
      duration: 0.14 + strength * 0.18,
      volume: 0.025 + strength * 0.045,
      startFrequency: 520 + strength * 380,
      endFrequency: 1250 + strength * 950,
      filterType: 'bandpass',
      q: 0.65,
    });
    this.playTone(context, this.sfxGain, {
      startFrequency: 165 + strength * 70,
      endFrequency: 430 + strength * 250,
      duration: 0.16 + strength * 0.1,
      volume: 0.025 + strength * 0.018,
      type: 'sine',
    });
  }

  rimHit(speed: number): void {
    const context = this.getPlayableContext();
    if (!context || !this.sfxGain) return;

    const impact = Math.min(1, Math.max(0, speed / 8));
    if (impact < 0.055 || context.currentTime - this.lastRimHitAt < 0.052) return;
    this.lastRimHitAt = context.currentTime;

    const variants = [1260, 1480, 1730, 1375];
    const baseFrequency = variants[this.rimVariant % variants.length] * (0.96 + impact * 0.08);
    this.rimVariant += 1;

    this.playTone(context, this.sfxGain, {
      startFrequency: baseFrequency,
      endFrequency: baseFrequency * 0.72,
      duration: 0.07 + impact * 0.08,
      volume: 0.018 + impact * 0.052,
      type: 'triangle',
    });
    this.playTone(context, this.sfxGain, {
      startFrequency: baseFrequency * 2.14,
      endFrequency: baseFrequency * 1.48,
      duration: 0.045 + impact * 0.045,
      volume: 0.008 + impact * 0.018,
      type: 'sine',
      delay: 0.004,
    });
    if (impact > 0.24) {
      this.playNoise(context, this.sfxGain, {
        duration: 0.035 + impact * 0.035,
        volume: 0.006 + impact * 0.014,
        startFrequency: 2100,
        endFrequency: 1350,
        filterType: 'bandpass',
        q: 1.8,
      });
    }
  }

  boardHit(speed = 3): void {
    const context = this.getPlayableContext();
    if (!context || !this.sfxGain) return;

    const impact = Math.min(1, Math.max(0.08, speed / 9));
    if (context.currentTime - this.lastBoardHitAt < 0.11) return;
    this.lastBoardHitAt = context.currentTime;

    this.playTone(context, this.sfxGain, {
      startFrequency: 112 + impact * 35,
      endFrequency: 54,
      duration: 0.18 + impact * 0.12,
      volume: 0.025 + impact * 0.048,
      type: 'sine',
    });
    this.playNoise(context, this.sfxGain, {
      duration: 0.085 + impact * 0.08,
      volume: 0.014 + impact * 0.025,
      startFrequency: 360,
      endFrequency: 190,
      filterType: 'lowpass',
      q: 0.6,
    });
  }

  normalCup(): void {
    const context = this.getPlayableContext();
    if (!context || !this.sfxGain) return;

    this.playTone(context, this.sfxGain, {
      startFrequency: 315,
      endFrequency: 142,
      duration: 0.27,
      volume: 0.052,
      type: 'sine',
    });
    this.playTone(context, this.sfxGain, {
      startFrequency: 590,
      endFrequency: 245,
      duration: 0.16,
      volume: 0.022,
      type: 'triangle',
      delay: 0.018,
    });
  }

  specialCup(): void {
    const context = this.getPlayableContext();
    if (!context || !this.sfxGain) return;

    const notes = [659.25, 830.61, 987.77, 1318.51];
    notes.forEach((frequency, index) => {
      this.playTone(context, this.sfxGain as GainNode, {
        startFrequency: frequency,
        endFrequency: frequency * 1.015,
        duration: 0.24 + index * 0.025,
        volume: 0.046 - index * 0.004,
        type: index % 2 === 0 ? 'triangle' : 'sine',
        delay: index * 0.078,
      });
    });
    this.playNoise(context, this.sfxGain, {
      duration: 0.34,
      volume: 0.018,
      startFrequency: 3400,
      endFrequency: 6200,
      filterType: 'highpass',
      q: 0.7,
      delay: 0.05,
    });
  }

  miss(): void {
    const context = this.getPlayableContext();
    if (!context || !this.sfxGain) return;

    this.playTone(context, this.sfxGain, {
      startFrequency: 420,
      endFrequency: 205,
      duration: 0.25,
      volume: 0.031,
      type: 'triangle',
    });
    this.playTone(context, this.sfxGain, {
      startFrequency: 278,
      endFrequency: 142,
      duration: 0.28,
      volume: 0.024,
      type: 'sine',
      delay: 0.09,
    });
  }

  roundOver(specialHits = 0): void {
    const context = this.getPlayableContext();
    if (!context || !this.sfxGain) return;

    const notes = specialHits > 0 ? [523.25, 659.25, 783.99, 1046.5] : [392, 329.63, 261.63];
    notes.forEach((frequency, index) => {
      this.playTone(context, this.sfxGain as GainNode, {
        startFrequency: frequency,
        endFrequency: frequency * (specialHits > 0 ? 1.01 : 0.98),
        duration: specialHits > 0 ? 0.35 : 0.3,
        volume: specialHits > 0 ? 0.035 : 0.025,
        type: 'triangle',
        delay: index * (specialHits > 0 ? 0.11 : 0.14),
      });
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeUnlockListeners();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    for (const source of this.ambienceSources) {
      try {
        source.stop();
      } catch {
        // The source may already have ended while the page was being torn down.
      }
      source.disconnect();
    }
    this.ambienceSources.length = 0;

    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        // Ignore sources whose scheduled stop already ran.
      }
      source.disconnect();
    }
    this.activeSources.clear();

    this.masterGain?.disconnect();
    this.sfxGain?.disconnect();
    this.uiGain?.disconnect();
    this.ambienceGain?.disconnect();
    void this.context?.close();

    this.noiseBuffer = null;
    this.masterGain = null;
    this.sfxGain = null;
    this.uiGain = null;
    this.ambienceGain = null;
    this.context = null;
    this.unlocked = false;
  }

  private readonly handleFirstGesture = (): void => {
    void this.unlock();
  };

  private readonly handleVisibilityChange = (): void => {
    const context = this.context;
    if (!context || context.state === 'closed') return;

    if (document.hidden) {
      void context.suspend();
    } else if (this.unlocked && !this.muted) {
      void this.unlock();
    }
  };

  private installUnlockListeners(): void {
    if (this.unlockListenersInstalled || this.disposed) return;
    window.addEventListener('pointerdown', this.handleFirstGesture, { capture: true, passive: true });
    window.addEventListener('keydown', this.handleFirstGesture, { capture: true });
    this.unlockListenersInstalled = true;
  }

  private removeUnlockListeners(): void {
    if (!this.unlockListenersInstalled) return;
    window.removeEventListener('pointerdown', this.handleFirstGesture, true);
    window.removeEventListener('keydown', this.handleFirstGesture, true);
    this.unlockListenersInstalled = false;
  }

  private createAudioGraph(context: AudioContext): void {
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -12;
    compressor.knee.value = 16;
    compressor.ratio.value = 5;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.2;

    this.masterGain = context.createGain();
    this.masterGain.gain.value = this.muted ? 0 : 0.88;
    this.sfxGain = context.createGain();
    this.sfxGain.gain.value = 0.9;
    this.uiGain = context.createGain();
    this.uiGain.gain.value = 0.72;
    this.ambienceGain = context.createGain();
    this.ambienceGain.gain.value = 0.2;

    this.sfxGain.connect(this.masterGain);
    this.uiGain.connect(this.masterGain);
    this.ambienceGain.connect(this.masterGain);
    this.masterGain.connect(compressor).connect(context.destination);
    this.noiseBuffer = this.createNoiseBuffer(context, 2);
  }

  private startAmbience(): void {
    const context = this.context;
    const destination = this.ambienceGain;
    if (!context || !destination || this.ambienceSources.length > 0 || this.disposed) return;

    const startAt = context.currentTime + 0.03;

    const crowdSource = context.createBufferSource();
    const crowdFilter = context.createBiquadFilter();
    const crowdGain = context.createGain();
    crowdSource.buffer = this.createCrowdBuffer(context, 10);
    crowdSource.loop = true;
    crowdFilter.type = 'lowpass';
    crowdFilter.frequency.value = 1080;
    crowdFilter.Q.value = 0.45;
    crowdGain.gain.value = 0.115;
    crowdSource.connect(crowdFilter).connect(crowdGain).connect(destination);

    const calliopeSource = context.createBufferSource();
    const calliopeFilter = context.createBiquadFilter();
    const calliopeGain = context.createGain();
    calliopeSource.buffer = this.createCalliopeBuffer(context, 12);
    calliopeSource.loop = true;
    calliopeFilter.type = 'lowpass';
    calliopeFilter.frequency.value = 2500;
    calliopeFilter.Q.value = 0.5;
    calliopeGain.gain.value = 0.09;
    calliopeSource.connect(calliopeFilter).connect(calliopeGain).connect(destination);

    crowdSource.start(startAt);
    calliopeSource.start(startAt);
    this.ambienceSources.push(crowdSource, calliopeSource);
  }

  private playTone(
    context: AudioContext,
    destination: GainNode,
    options: {
      startFrequency: number;
      endFrequency: number;
      duration: number;
      volume: number;
      type: OscillatorType;
      delay?: number;
    },
  ): void {
    const startAt = context.currentTime + (options.delay ?? 0);
    const endAt = startAt + options.duration;
    const attack = Math.min(0.012, options.duration * 0.2);
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = options.type;
    oscillator.frequency.setValueAtTime(Math.max(20, options.startFrequency), startAt);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, options.endFrequency), endAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.linearRampToValueAtTime(options.volume, startAt + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

    oscillator.connect(gain).connect(destination);
    this.trackSource(oscillator);
    oscillator.start(startAt);
    oscillator.stop(endAt + 0.015);
  }

  private playNoise(
    context: AudioContext,
    destination: GainNode,
    options: {
      duration: number;
      volume: number;
      startFrequency: number;
      endFrequency: number;
      filterType: BiquadFilterType;
      q: number;
      delay?: number;
    },
  ): void {
    if (!this.noiseBuffer) return;
    const startAt = context.currentTime + (options.delay ?? 0);
    const endAt = startAt + options.duration;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    source.buffer = this.noiseBuffer;
    filter.type = options.filterType;
    filter.Q.value = options.q;
    filter.frequency.setValueAtTime(Math.max(30, options.startFrequency), startAt);
    filter.frequency.exponentialRampToValueAtTime(Math.max(30, options.endFrequency), endAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.linearRampToValueAtTime(options.volume, startAt + Math.min(0.014, options.duration * 0.25));
    gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

    source.connect(filter).connect(gain).connect(destination);
    this.trackSource(source);
    const maxOffset = Math.max(0, this.noiseBuffer.duration - options.duration - 0.01);
    source.start(startAt, Math.random() * maxOffset, options.duration);
  }

  private trackSource(source: AudioScheduledSourceNode): void {
    this.activeSources.add(source);
    source.addEventListener(
      'ended',
      () => {
        this.activeSources.delete(source);
        source.disconnect();
      },
      { once: true },
    );
  }

  private getPlayableContext(): AudioContext | null {
    const context = this.context;
    if (this.disposed || this.muted || !context) return null;
    if (context.state !== 'running') {
      void this.unlock();
      return null;
    }
    return context;
  }

  private applyMuteLevel(immediate: boolean): void {
    const context = this.context;
    const master = this.masterGain;
    if (!context || !master || context.state === 'closed') return;

    const now = context.currentTime;
    const target = this.muted ? 0 : 0.88;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);

    if (immediate) {
      master.gain.setValueAtTime(target, now);
    } else {
      master.gain.linearRampToValueAtTime(target, now + 0.045);
    }
  }

  private createNoiseBuffer(context: AudioContext, duration: number): AudioBuffer {
    const length = Math.ceil(context.sampleRate * duration);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    let previous = 0;

    for (let index = 0; index < length; index += 1) {
      const white = Math.random() * 2 - 1;
      previous = previous * 0.28 + white * 0.72;
      data[index] = previous;
    }

    return buffer;
  }

  private createCrowdBuffer(context: AudioContext, duration: number): AudioBuffer {
    const length = Math.ceil(context.sampleRate * duration);
    const buffer = context.createBuffer(2, length, context.sampleRate);
    let seed = 0x71c0ffee;
    const random = (): number => {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      return seed / 0xffffffff;
    };

    for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
      const raw = new Float32Array(length);
      for (let index = 0; index < length; index += 1) raw[index] = random() * 2 - 1;
      const murmur = this.circularSmooth(raw, 96 + channel * 17);
      const rumble = this.circularSmooth(raw, 420 + channel * 31);
      const output = buffer.getChannelData(channel);

      for (let index = 0; index < length; index += 1) {
        const phase = index / length;
        const swell =
          0.72 +
          0.12 * Math.sin(TAU * (phase * 3 + channel * 0.17)) +
          0.08 * Math.sin(TAU * (phase * 7 + 0.31));
        output[index] = (murmur[index] * 1.75 + rumble[index] * 2.4) * swell;
      }
    }

    return buffer;
  }

  private createCalliopeBuffer(context: AudioContext, duration: number): AudioBuffer {
    const length = Math.ceil(context.sampleRate * duration);
    const buffer = context.createBuffer(2, length, context.sampleRate);
    const melody = [72, 76, 79, 76, 74, 77, 81, 77, 72, 76, 79, 84, 81, 77, 74, 67];
    const bass = [48, 55, 50, 55, 48, 55, 50, 55];
    const steps = 32;

    for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
      const output = buffer.getChannelData(channel);

      for (let index = 0; index < length; index += 1) {
        const loopPhase = index / length;
        const stepPosition = loopPhase * steps;
        const step = Math.floor(stepPosition) % steps;
        const notePhase = stepPosition - Math.floor(stepPosition);
        const envelope = Math.sin(Math.PI * Math.min(1, notePhase / 0.82)) ** 2;
        const melodyFrequency = 440 * 2 ** ((melody[step % melody.length] - 69) / 12);
        const seconds = index / context.sampleRate;
        const melodyWave =
          Math.sin(TAU * melodyFrequency * seconds + channel * 0.08) +
          0.23 * Math.sin(TAU * melodyFrequency * 2 * seconds + 0.4);

        const bassStepPosition = loopPhase * 16;
        const bassStep = Math.floor(bassStepPosition) % 16;
        const bassPhase = bassStepPosition - Math.floor(bassStepPosition);
        const bassEnvelope = Math.sin(Math.PI * Math.min(1, bassPhase / 0.62)) ** 2;
        const bassFrequency = 440 * 2 ** ((bass[bassStep % bass.length] - 69) / 12);
        const bassWave = Math.sin(TAU * bassFrequency * seconds);

        output[index] = melodyWave * envelope * 0.12 + bassWave * bassEnvelope * 0.055;
      }
    }

    return buffer;
  }

  private circularSmooth(input: Float32Array, radius: number): Float32Array {
    const output = new Float32Array(input.length);
    const windowSize = radius * 2 + 1;
    let sum = 0;

    for (let offset = -radius; offset <= radius; offset += 1) {
      sum += input[(offset + input.length) % input.length];
    }

    for (let index = 0; index < input.length; index += 1) {
      output[index] = sum / windowSize;
      const removeIndex = (index - radius + input.length) % input.length;
      const addIndex = (index + radius + 1) % input.length;
      sum += input[addIndex] - input[removeIndex];
    }

    return output;
  }

  private normalizePower(power: number): number {
    if (!Number.isFinite(power)) return 0;
    const normalized = power > 1 ? power / 100 : power;
    return Math.min(1, Math.max(0, normalized));
  }

  private getAudioContextClass(): AudioContextConstructor | null {
    const extendedWindow = window as typeof window & {
      webkitAudioContext?: AudioContextConstructor;
    };
    return window.AudioContext ?? extendedWindow.webkitAudioContext ?? null;
  }

  private readStoredMute(): boolean {
    try {
      return window.localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private storeMute(muted: boolean): void {
    try {
      window.localStorage.setItem(MUTE_STORAGE_KEY, String(muted));
    } catch {
      // Storage can be unavailable in private or embedded browser contexts.
    }
  }
}
